import "./styles.css";
import catalogData from "./data/catalog.json";
import {
  attemptNodeSelection,
  buildInsightReport,
  classifyNodes,
} from "./domain/engine";
import { buildArcPath, layoutConstellation } from "./domain/layout";
import type {
  ClassifiedNode,
  KnowledgeCatalog,
  LocalizedText,
  NodeState,
} from "./domain/types";

type Locale = "en" | "zh";

const catalog = catalogData as KnowledgeCatalog;
const requireElement = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Required element is missing: ${selector}`);
  return element;
};

const app = requireElement<HTMLElement>("#app");

let locale: Locale = "en";
let selectedIds: string[] = [];
let inspectedId = "BASE-A";
let announcement = "";
let catalogQuery = "";

const copy = {
  en: {
    eyebrow: "Evidence-aware knowledge navigator",
    title: "BioFormulation Constellation",
    subtitle:
      "Select synthetic functional roles. Explore candidates move inward; potential conflicts are pushed aside with an explanation.",
    boundary: "No formulas, percentages, safety verdicts, or AI-generated recipes.",
    synthetic: "Synthetic demo data",
    graphTitle: "Knowledge constellation",
    graphHint: "Select a node to reorganize the map.",
    clear: "Clear selection",
    selected: "Selected",
    explore: "Explore",
    conditional: "Needs context",
    review: "Conflict to review",
    blocked: "Potential conflict",
    unknown: "Evidence unknown",
    available: "Starting point",
    core: "Current\nselection",
    details: "Why this node moved",
    noRelation: "No relationship is asserted for this node in the current synthetic dataset.",
    startHint: "Choose this node as a starting point to reveal declared relationships.",
    selectedHint: "This node is selected. Activate it again to remove it.",
    blockedHint: "Selection is blocked by the demo rule. Activation only opens this explanation.",
    reviewHint: "This soft conflict needs an explicit review step before it can enter the selection.",
    addReview: "Add as review-only candidate",
    conditionalHint: "Required conditions are missing. Review them before adding this node as pending.",
    addConditional: "Add as condition-pending candidate",
    unknownHint: "No relationship evidence is asserted. Review this gap before adding the node.",
    addUnknown: "Add as evidence-unknown candidate",
    listTitle: "Equivalent list view",
    listHint: "The list offers the same selection and explanation actions without spatial motion.",
    insightTitle: "Selection insight",
    emptyInsight: "Select two or more roles to inspect effects, conditions, and unknowns.",
    intendedRoles: "Declared functional roles",
    effects: "Knowledge-base effect hypotheses",
    noEffects: "No effect rule is fully matched.",
    reviewSection: "Conditions and conflicts",
    noReview: "No declared conflict is present; this is not proof of compatibility.",
    unknownPairs: "Unknown relationships",
    nextChecks: "What to verify next",
    evidence: "Evidence records",
    conclusion: "This navigator cannot conclude feasibility, safety, stability, efficacy, or compliance.",
    selectedCount: "selected",
    remove: "Remove",
    relation: "Related to",
    action: "Next action",
    noSelection: "No nodes selected",
    dataToolsTitle: "Catalog tools",
    dataToolsHint: "Search the bundled catalog or preview a local JSON/CSV import. Nothing is uploaded or stored.",
    searchLabel: "Search nodes",
    importLabel: "Preview import",
    importEmpty: "No file selected.",
    importSummary: (rows: number, errors: number) => `Preview: ${rows} rows · ${errors} rejected`,
    importError: "This file is not a supported JSON/CSV catalog.",
    footer:
      "Open-source interaction engine and synthetic taxonomy. Real assertions require conditions, provenance, licence, curator review, and evidence.",
  },
  zh: {
    eyebrow: "证据感知型知识导航器",
    title: "生物配方星图",
    subtitle: "选择合成功能角色。可探索节点向内聚拢，潜在冲突节点会被推到外围并说明原因。",
    boundary: "不提供配方、比例、安全结论或 AI 自动生成方案。",
    synthetic: "合成演示数据",
    graphTitle: "知识星图",
    graphHint: "选择一个节点，星图会按已声明关系重新排列。",
    clear: "清空选择",
    selected: "已选择",
    explore: "可探索",
    conditional: "需要条件",
    review: "冲突待审",
    blocked: "潜在冲突",
    unknown: "证据未知",
    available: "起始节点",
    core: "当前\n选择",
    details: "为什么节点会移动",
    noRelation: "当前合成数据集没有声明该节点与所选节点之间的关系。",
    startHint: "选择此节点作为起点，可以显示已声明的关系。",
    selectedHint: "此节点已选择；再次点击即可移除。",
    blockedHint: "演示规则已阻止该选择；点击只会显示解释，不会加入组合。",
    reviewHint: "这个软冲突需要经过明确复核，才能作为待审节点加入选择。",
    addReview: "作为待审节点加入",
    conditionalHint: "当前缺少必要条件；复核后才能把该节点作为条件待补项加入。",
    addConditional: "作为条件待补项加入",
    unknownHint: "知识库没有声明关系证据；复核这一缺口后才能加入。",
    addUnknown: "作为证据未知项加入",
    listTitle: "等价列表视图",
    listHint: "列表提供与空间星图相同的选择和解释功能，不依赖动画。",
    insightTitle: "组合见解",
    emptyInsight: "至少选择两个角色，才能检查功能假设、条件和未知关系。",
    intendedRoles: "已声明功能角色",
    effects: "知识库效果假设",
    noEffects: "当前没有完整命中的效果规则。",
    reviewSection: "条件与冲突",
    noReview: "当前没有已声明冲突；这并不等于已经证明兼容。",
    unknownPairs: "未知关系",
    nextChecks: "下一步需要验证",
    evidence: "证据记录",
    conclusion: "本导航器不能对可行性、安全性、稳定性、功效或合规性作出结论。",
    selectedCount: "个已选择",
    remove: "移除",
    relation: "关联节点",
    action: "下一步",
    noSelection: "尚未选择节点",
    dataToolsTitle: "知识库工具",
    dataToolsHint: "搜索内置目录，或预览本地 JSON/CSV 导入。文件不会上传，也不会写入浏览器存储。",
    searchLabel: "搜索节点",
    importLabel: "预览导入",
    importEmpty: "尚未选择文件。",
    importSummary: (rows: number, errors: number) => `预览：${rows} 行 · ${errors} 行拒绝`,
    importError: "文件不是受支持的 JSON/CSV 知识目录。",
    footer: "开源交互引擎与合成分类体系。真实断言必须包含条件、溯源、许可、审核人和证据。",
  },
} as const;

const statusOrder: NodeState[] = [
  "selected",
  "explore",
  "conditional",
  "review",
  "blocked",
  "unknown",
  "available",
];

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>'"]/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#039;",
        '"': "&quot;",
      })[character] ?? character,
  );

const localize = (value: LocalizedText): string => value[locale];
const labelForState = (state: NodeState): string => copy[locale][state];
const evidenceLinks = (ids: string[]): string =>
  ids
    .map((id) => {
      const record = catalog.evidence.find((item) => item.id === id);
      return record
        ? `<a href="${escapeHtml(record.uri)}" rel="noreferrer">${escapeHtml(record.id)}</a>`
        : `<code>${escapeHtml(id)}</code>`;
    })
    .join(" ");

app.innerHTML = `
  <div class="app-shell">
    <header class="hero">
      <div class="hero-copy">
        <p class="eyebrow" id="eyebrow"></p>
        <h1 id="page-title"></h1>
        <p class="hero-subtitle" id="page-subtitle"></p>
        <div class="boundary-line">
          <span class="boundary-badge" id="synthetic-badge"></span>
          <span id="boundary-copy"></span>
        </div>
      </div>
      <div class="language-switch" aria-label="Language / 语言">
        <button type="button" data-locale="en" aria-pressed="true">EN</button>
        <button type="button" data-locale="zh" aria-pressed="false">中文</button>
      </div>
    </header>

    <main>
      <section class="data-tools" aria-labelledby="data-tools-title">
        <div class="section-heading"><div><h2 id="data-tools-title"></h2><p id="data-tools-hint"></p></div></div>
        <div class="data-tools-row">
          <label class="search-field" for="catalog-search"><span id="search-label"></span><input id="catalog-search" type="search" autocomplete="off" /></label>
          <label class="import-field" for="catalog-import"><span id="import-label"></span><input id="catalog-import" type="file" accept=".json,.csv,application/json,text/csv" /></label>
        </div>
        <div id="import-preview" class="import-preview" role="status" aria-live="polite"></div>
      </section>
      <section class="graph-section" aria-labelledby="graph-title">
        <div class="section-heading">
          <div>
            <h2 id="graph-title"></h2>
            <p id="graph-hint"></p>
          </div>
          <button class="clear-button" id="clear-selection" type="button"></button>
        </div>
        <div class="status-strip" id="status-strip" aria-label="Node status counts"></div>
        <div class="graph-stage" id="graph-stage" role="group" aria-describedby="graph-description">
          <p class="sr-only" id="graph-description"></p>
          <svg class="graph-links" id="graph-links" aria-hidden="true"></svg>
          <div class="graph-core" id="graph-core" aria-hidden="true"></div>
          <div id="graph-nodes"></div>
        </div>
        <div class="legend" id="legend" aria-label="Constellation legend"></div>
      </section>

      <section class="detail-section" aria-labelledby="detail-title">
        <h2 id="detail-title"></h2>
        <div id="node-detail"></div>
      </section>

      <section class="selection-section" aria-labelledby="insight-title">
        <div class="section-heading">
          <div>
            <h2 id="insight-title"></h2>
            <p class="selection-count" id="selection-count"></p>
          </div>
          <div class="selected-chips" id="selected-chips"></div>
        </div>
        <div id="insight-report"></div>
      </section>

      <details class="list-section" id="list-section">
        <summary id="list-title"></summary>
        <p id="list-hint"></p>
        <div class="state-list" id="state-list"></div>
      </details>
    </main>

    <footer>
      <p id="footer-copy"></p>
      <a href="https://github.com/ProfesseurHaipeng/bioformulation-constellation" rel="noreferrer">GitHub</a>
      <span aria-hidden="true">·</span>
      <a href="https://github.com/ProfesseurHaipeng/bioformulation-constellation/blob/main/docs/KNOWLEDGE_MODEL.md" rel="noreferrer">Knowledge model</a>
    </footer>
    <div class="sr-only" id="announcement" aria-live="polite"></div>
  </div>`;

const stage = requireElement<HTMLElement>("#graph-stage");
const graphNodes = requireElement<HTMLElement>("#graph-nodes");
const graphLinks = requireElement<SVGSVGElement>("#graph-links");

for (const node of catalog.nodes) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "graph-node";
  button.dataset.nodeId = node.id;
  button.innerHTML = `<span class="node-category"></span><strong></strong><span class="node-state"></span>`;
  graphNodes.append(button);
}

function classifiedById(classified: ClassifiedNode[]): Map<string, ClassifiedNode> {
  return new Map(classified.map((item) => [item.node.id, item]));
}

function setText(id: string, value: string): void {
  const element = document.querySelector<HTMLElement>(`#${id}`);
  if (element) element.textContent = value;
}

function statusCounts(classified: ClassifiedNode[]): string {
  return statusOrder
    .filter((state) => state !== "available" || selectedIds.length === 0)
    .map((state) => {
      const count = classified.filter((item) => item.state === state).length;
      if (count === 0 && state !== "selected") return "";
      return `<span class="status-count state-${state}"><b>${count}</b> ${escapeHtml(labelForState(state))}</span>`;
    })
    .join("");
}

function renderLegend(): void {
  const states: NodeState[] = selectedIds.length
    ? ["selected", "explore", "conditional", "blocked", "unknown"]
    : ["available"];
  const legend = document.querySelector<HTMLElement>("#legend");
  if (!legend) return;
  legend.innerHTML = states
    .map(
      (state) =>
        `<span><i class="legend-mark state-${state}" aria-hidden="true"></i>${escapeHtml(labelForState(state))}</span>`,
    )
    .join("");
}

function updateGraph(classified: ClassifiedNode[]): void {
  const byId = classifiedById(classified);
  const positions = layoutConstellation(
    classified,
    stage.clientWidth,
    stage.clientHeight,
  );

  for (const button of graphNodes.querySelectorAll<HTMLButtonElement>(".graph-node")) {
    const id = button.dataset.nodeId ?? "";
    const item = byId.get(id);
    const position = positions.get(id);
    if (!item || !position) continue;
    const previousX = Number.parseFloat(button.style.left);
    const previousY = Number.parseFloat(button.style.top);
    const previousState = button.dataset.state;
    button.getAnimations().forEach((animation) => animation.cancel());
    button.className = `graph-node state-${item.state}`;
    button.style.left = `${position.x}px`;
    button.style.top = `${position.y}px`;
    const shouldRouteAroundCore =
      ["review", "blocked"].includes(item.state) &&
      previousState !== undefined &&
      previousState !== item.state &&
      Number.isFinite(previousX) &&
      Number.isFinite(previousY) &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (shouldRouteAroundCore) {
      const path = buildArcPath(
        { x: previousX, y: previousY },
        position,
        { x: stage.clientWidth / 2, y: stage.clientHeight / 2 },
        145,
        9,
      );
      button.classList.add("is-rerouting");
      const animation = button.animate(
        path.map((point) => ({ left: `${point.x}px`, top: `${point.y}px` })),
        { duration: 680, easing: "linear" },
      );
      void animation.finished
        .catch(() => undefined)
        .finally(() => button.classList.remove("is-rerouting"));
    }
    button.dataset.state = item.state;
    button.tabIndex = id === inspectedId ? 0 : -1;
    button.setAttribute("aria-pressed", String(item.state === "selected"));
    button.setAttribute("aria-disabled", String(item.state === "blocked"));
    button.setAttribute(
      "aria-label",
      `${localize(item.node.label)} — ${labelForState(item.state)}`,
    );
    button.querySelector<HTMLElement>(".node-category")!.textContent = localize(
      item.node.category,
    );
    button.querySelector<HTMLElement>("strong")!.textContent = localize(
      item.node.label,
    );
    button.querySelector<HTMLElement>(".node-state")!.textContent = labelForState(
      item.state,
    );
  }

  const centerX = stage.clientWidth / 2;
  const centerY = stage.clientHeight / 2;
  graphLinks.setAttribute("viewBox", `0 0 ${stage.clientWidth} ${stage.clientHeight}`);
  graphLinks.innerHTML = classified
    .filter((item) =>
      ["explore", "conditional", "review", "blocked"].includes(item.state),
    )
    .map((item) => {
      const position = positions.get(item.node.id);
      if (!position) return "";
      return `<line class="link-${item.state}" x1="${centerX}" y1="${centerY}" x2="${position.x}" y2="${position.y}" />`;
    })
    .join("");
}

function detailMarkup(item: ClassifiedNode): string {
  const stateHint =
    item.state === "blocked"
      ? copy[locale].blockedHint
      : item.state === "review"
        ? copy[locale].reviewHint
        : item.state === "conditional"
          ? copy[locale].conditionalHint
          : item.state === "unknown"
            ? copy[locale].unknownHint
      : item.state === "selected"
        ? copy[locale].selectedHint
        : item.state === "available"
          ? copy[locale].startHint
          : copy[locale].noRelation;
  const explanations = item.explanations.length
    ? `<div class="relation-stack">${item.explanations
        .map((explanation) => {
          const selected = catalog.nodes.find(
            (node) => node.id === explanation.selectedNodeId,
          );
          return `<article class="relation-note state-${item.state}">
            <p><b>${escapeHtml(copy[locale].relation)}:</b> ${escapeHtml(selected ? localize(selected.label) : explanation.selectedNodeId)}</p>
            <p>${escapeHtml(localize(explanation.reason))}</p>
            <p><b>${escapeHtml(copy[locale].action)}:</b> ${escapeHtml(localize(explanation.action))}</p>
            ${
              explanation.conditions.length
                ? `<ul>${explanation.conditions.map((condition) => `<li>${escapeHtml(localize(condition))}</li>`).join("")}</ul>`
                : ""
            }
            <p class="rule-meta"><code>${escapeHtml(explanation.relationId)} · v${escapeHtml(explanation.ruleVersion)} · ${escapeHtml(explanation.strength)} · ${escapeHtml(explanation.lastReviewed)}</code></p>
            <p class="evidence-links">${evidenceLinks(explanation.evidenceIds)}</p>
          </article>`;
        })
        .join("")}</div>`
    : `<p class="muted-copy">${escapeHtml(stateHint)}</p>`;
  const reviewLabel =
    item.state === "review"
      ? copy[locale].addReview
      : item.state === "conditional"
        ? copy[locale].addConditional
        : item.state === "unknown"
          ? copy[locale].addUnknown
          : "";
  const reviewAction = reviewLabel
    ? `<button class="review-button" type="button" data-allow-review="${escapeHtml(item.node.id)}">${escapeHtml(reviewLabel)}</button>`
    : "";
  return `<div class="detail-card">
    <div class="detail-title-row">
      <span class="state-pill state-${item.state}">${escapeHtml(labelForState(item.state))}</span>
      <code>${escapeHtml(item.node.id)}</code>
    </div>
    <h3>${escapeHtml(localize(item.node.label))}</h3>
    <p>${escapeHtml(localize(item.node.description))}</p>
    ${explanations}
    ${reviewAction}
  </div>`;
}

function renderInsight(): void {
  const report = buildInsightReport(catalog, selectedIds);
  setText(
    "selection-count",
    locale === "en"
      ? `${selectedIds.length} ${copy.en.selectedCount}`
      : `${selectedIds.length} ${copy.zh.selectedCount}`,
  );

  const chips = document.querySelector<HTMLElement>("#selected-chips");
  if (chips) {
    chips.innerHTML = report.selectedNodes.length
      ? report.selectedNodes
          .map(
            (node) =>
              `<button type="button" data-remove-node="${escapeHtml(node.id)}">${escapeHtml(localize(node.label))}<span aria-hidden="true">×</span><span class="sr-only">${escapeHtml(copy[locale].remove)}</span></button>`,
          )
          .join("")
      : `<span class="muted-copy">${escapeHtml(copy[locale].noSelection)}</span>`;
  }

  const target = document.querySelector<HTMLElement>("#insight-report");
  if (!target) return;
  if (selectedIds.length === 0) {
    target.innerHTML = `<div class="empty-insight"><p>${escapeHtml(copy[locale].emptyInsight)}</p></div>`;
    return;
  }

  const effects = report.matchedEffects.length
    ? `<ul>${report.matchedEffects
        .map(
          (effect) =>
            `<li><strong>${escapeHtml(localize(effect.title))}</strong><span>${escapeHtml(localize(effect.explanation))}</span><code>${escapeHtml(effect.id)} · ${escapeHtml(effect.strength)}</code></li>`,
        )
        .join("")}</ul>`
    : `<p class="muted-copy">${escapeHtml(copy[locale].noEffects)}</p>`;
  const reviews = [...report.conflicts, ...report.conditions];
  const reviewMarkup = reviews.length
    ? `<ul>${reviews
        .map(
          (relation) =>
            `<li><strong>${escapeHtml(relation.id)} · ${escapeHtml(relation.kind)}</strong><span>${escapeHtml(localize(relation.reason))}</span></li>`,
        )
        .join("")}</ul>`
    : `<p class="muted-copy">${escapeHtml(copy[locale].noReview)}</p>`;
  const evidenceMarkup = report.evidenceIds.length
    ? `<ul class="evidence-list">${report.evidenceIds
        .map((id) => {
          const record = catalog.evidence.find((item) => item.id === id);
          if (!record) return `<li><code>${escapeHtml(id)}</code></li>`;
          return `<li>
            <a href="${escapeHtml(record.uri)}" rel="noreferrer"><strong>${escapeHtml(record.id)} · ${escapeHtml(record.title)}</strong></a>
            <span>${escapeHtml(record.organization)} · ${escapeHtml(record.license)} · ${escapeHtml(record.evidenceCode)}</span>
            <p>${escapeHtml(localize(record.note))}</p>
          </li>`;
        })
        .join("")}</ul>`
    : "—";

  target.innerHTML = `<div class="insight-grid">
    <article>
      <h3>${escapeHtml(copy[locale].intendedRoles)}</h3>
      <div class="role-list">${report.functionalRoles.map((role) => `<code>${escapeHtml(role)}</code>`).join("")}</div>
    </article>
    <article>
      <h3>${escapeHtml(copy[locale].effects)}</h3>
      ${effects}
    </article>
    <article>
      <h3>${escapeHtml(copy[locale].reviewSection)}</h3>
      ${reviewMarkup}
    </article>
    <article>
      <h3>${escapeHtml(copy[locale].unknownPairs)}</h3>
      ${
        report.unknownPairs.length
          ? `<p><strong>${report.unknownPairs.length}</strong> ${locale === "en" ? "selected pairs have no asserted relationship:" : "组已选节点没有已声明关系："}</p><ul>${report.unknownPairs
              .slice(0, 8)
              .map((pair) => {
                const left = catalog.nodes.find((node) => node.id === pair.left);
                const right = catalog.nodes.find((node) => node.id === pair.right);
                return `<li><code>${escapeHtml(pair.left)} ↔ ${escapeHtml(pair.right)}</code><span>${escapeHtml(left ? localize(left.label) : pair.left)} / ${escapeHtml(right ? localize(right.label) : pair.right)}</span></li>`;
              })
              .join("")}</ul>`
          : `<p class="muted-copy">${locale === "en" ? "Every selected pair has an asserted synthetic relationship." : "每组已选节点都有已声明的合成关系。"}</p>`
      }
    </article>
    <article>
      <h3>${escapeHtml(copy[locale].nextChecks)}</h3>
      <ul>${report.nextChecks.map((item) => `<li>${escapeHtml(localize(item))}</li>`).join("")}</ul>
    </article>
    <article>
      <h3>${escapeHtml(copy[locale].evidence)}</h3>
      ${evidenceMarkup}
    </article>
  </div>
  <p class="conclusion-boundary">${escapeHtml(copy[locale].conclusion)}</p>`;
}

function renderList(classified: ClassifiedNode[]): void {
  const target = document.querySelector<HTMLElement>("#state-list");
  if (!target) return;
  const query = catalogQuery.trim().toLocaleLowerCase();
  const filtered = classified.filter((item) => {
    if (!query) return true;
    return [item.node.id, localize(item.node.label), localize(item.node.category), localize(item.node.description)]
      .join(" ").toLocaleLowerCase().includes(query);
  });
  target.innerHTML = statusOrder
    .map((state) => {
      const items = filtered.filter((item) => item.state === state);
      if (items.length === 0) return "";
      return `<section><h3>${escapeHtml(labelForState(state))} <span>${items.length}</span></h3>
        <div>${items
          .map(
            (item) =>
              `<button type="button" class="list-node state-${state}" data-list-node="${escapeHtml(item.node.id)}" aria-disabled="${state === "blocked"}" aria-pressed="${state === "selected"}">
                <strong>${escapeHtml(localize(item.node.label))}</strong>
                <span>${escapeHtml(localize(item.node.category))}</span>
              </button>`,
          )
          .join("")}</div></section>`;
    })
    .join("");
}

function renderLanguageCopy(): void {
  setText("eyebrow", copy[locale].eyebrow);
  setText("page-title", copy[locale].title);
  setText("page-subtitle", copy[locale].subtitle);
  setText("synthetic-badge", copy[locale].synthetic);
  setText("boundary-copy", copy[locale].boundary);
  setText("graph-title", copy[locale].graphTitle);
  setText("graph-hint", copy[locale].graphHint);
  setText("clear-selection", copy[locale].clear);
  setText("graph-description", copy[locale].graphHint);
  setText("graph-core", copy[locale].core);
  setText("detail-title", copy[locale].details);
  setText("insight-title", copy[locale].insightTitle);
  setText("list-title", copy[locale].listTitle);
  setText("list-hint", copy[locale].listHint);
  setText("data-tools-title", copy[locale].dataToolsTitle);
  setText("data-tools-hint", copy[locale].dataToolsHint);
  setText("search-label", copy[locale].searchLabel);
  setText("import-label", copy[locale].importLabel);
  const search = document.querySelector<HTMLInputElement>("#catalog-search");
  if (search && search.value !== catalogQuery) search.value = catalogQuery;
  setText("footer-copy", copy[locale].footer);
  document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  document.title = `${copy[locale].title} · Professeur Haipeng`;
}

function renderAll(): void {
  renderLanguageCopy();
  const classified = classifyNodes(catalog, selectedIds);
  updateGraph(classified);
  renderLegend();
  const statusStrip = document.querySelector<HTMLElement>("#status-strip");
  if (statusStrip) statusStrip.innerHTML = statusCounts(classified);
  const detail = document.querySelector<HTMLElement>("#node-detail");
  const inspected = classified.find((item) => item.node.id === inspectedId) ?? classified[0];
  if (detail && inspected) detail.innerHTML = detailMarkup(inspected);
  renderInsight();
  renderList(classified);
  const clear = document.querySelector<HTMLButtonElement>("#clear-selection");
  if (clear) clear.disabled = selectedIds.length === 0;
  setText("announcement", announcement);
}

function handleNode(
  nodeId: string,
  source: "graph" | "list" | "chip" | "detail",
  allowReview = false,
): void {
  inspectedId = nodeId;
  const node = catalog.nodes.find((item) => item.id === nodeId);
  const result = attemptNodeSelection(catalog, selectedIds, nodeId, {
    allowReview,
  });
  if (source === "chip" && selectedIds.includes(nodeId)) {
    selectedIds = result.selectedIds;
  } else if (result.accepted) {
    selectedIds = result.selectedIds;
  }
  const label = node ? localize(node.label) : nodeId;
  const pendingMessage =
    result.state === "conditional"
      ? copy[locale].conditionalHint
      : result.state === "unknown"
        ? copy[locale].unknownHint
        : copy[locale].reviewHint;
  announcement = result.accepted
    ? selectedIds.includes(nodeId)
      ? `${label}: ${copy[locale].selected}`
      : `${label}: ${copy[locale].remove}`
    : result.requiresReview
      ? `${label}: ${pendingMessage}`
      : `${label}: ${copy[locale].blockedHint}`;
  renderAll();
  const selector =
    source === "list"
      ? `[data-list-node="${CSS.escape(nodeId)}"]`
      : `[data-node-id="${CSS.escape(nodeId)}"]`;
  requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(selector)?.focus());
}

graphNodes.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-node-id]");
  if (button?.dataset.nodeId) handleNode(button.dataset.nodeId, "graph");
});

graphNodes.addEventListener("keydown", (event) => {
  const current = (event.target as Element).closest<HTMLButtonElement>("[data-node-id]");
  if (!current) return;
  const buttons = [...graphNodes.querySelectorAll<HTMLButtonElement>("[data-node-id]")];
  if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(event.key)) {
    event.preventDefault();
    const currentRect = current.getBoundingClientRect();
    const currentCenter = {
      x: currentRect.left + currentRect.width / 2,
      y: currentRect.top + currentRect.height / 2,
    };
    const candidates = buttons
      .filter((button) => button !== current)
      .map((button) => {
        const rect = button.getBoundingClientRect();
        const deltaX = rect.left + rect.width / 2 - currentCenter.x;
        const deltaY = rect.top + rect.height / 2 - currentCenter.y;
        const inDirection =
          (event.key === "ArrowRight" && deltaX > 0) ||
          (event.key === "ArrowLeft" && deltaX < 0) ||
          (event.key === "ArrowDown" && deltaY > 0) ||
          (event.key === "ArrowUp" && deltaY < 0);
        const primary = ["ArrowRight", "ArrowLeft"].includes(event.key)
          ? Math.abs(deltaX)
          : Math.abs(deltaY);
        const cross = ["ArrowRight", "ArrowLeft"].includes(event.key)
          ? Math.abs(deltaY)
          : Math.abs(deltaX);
        return { button, inDirection, score: primary + cross * 1.7 };
      })
      .filter((item) => item.inDirection)
      .sort((left, right) => left.score - right.score);
    const target = candidates[0]?.button;
    if (target?.dataset.nodeId) {
      inspectedId = target.dataset.nodeId;
      renderAll();
      requestAnimationFrame(() =>
        document
          .querySelector<HTMLButtonElement>(
            `[data-node-id="${CSS.escape(inspectedId)}"]`,
          )
          ?.focus(),
      );
    }
  }
  if (["Delete", "Backspace"].includes(event.key) && current.getAttribute("aria-pressed") === "true") {
    event.preventDefault();
    if (current.dataset.nodeId) handleNode(current.dataset.nodeId, "graph");
  }
});

document.querySelector("#state-list")?.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-list-node]");
  if (button?.dataset.listNode) handleNode(button.dataset.listNode, "list");
});

document.querySelector("#selected-chips")?.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-remove-node]");
  if (button?.dataset.removeNode) handleNode(button.dataset.removeNode, "chip");
});

document.querySelector("#node-detail")?.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "[data-allow-review]",
  );
  if (button?.dataset.allowReview) {
    handleNode(button.dataset.allowReview, "detail", true);
  }
});

document.querySelector("#clear-selection")?.addEventListener("click", () => {
  selectedIds = [];
  announcement = copy[locale].noSelection;
  renderAll();
});

document.querySelector("#catalog-search")?.addEventListener("input", (event) => {
  catalogQuery = (event.target as HTMLInputElement).value;
  renderList(classifyNodes(catalog, selectedIds));
});

document.querySelector("#catalog-import")?.addEventListener("change", async (event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  const target = document.querySelector<HTMLElement>("#import-preview");
  if (!target) return;
  if (!file) { target.textContent = copy[locale].importEmpty; return; }
  try {
    const text = await file.text();
    const parsed = file.name.toLowerCase().endsWith(".csv")
      ? text.split(/\r?\n/).filter(Boolean).slice(1)
      : (JSON.parse(text).rows ?? JSON.parse(text).nodes ?? JSON.parse(text));
    const rows = Array.isArray(parsed) ? parsed : [];
    const errors = rows.filter((row) => /(?:percentage|percent|quantity|amount|ratio|recipe|formula|配方|比例|浓度|投料|用量|cas|inci|supplier|供应商)/i.test(JSON.stringify(row))).length;
    target.innerHTML = `<strong>${escapeHtml(file.name)}</strong> · ${escapeHtml(copy[locale].importSummary(rows.length, errors))}`;
  } catch { target.textContent = copy[locale].importError; }
});

document.querySelector(".language-switch")?.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("[data-locale]");
  if (!button) return;
  locale = button.dataset.locale === "zh" ? "zh" : "en";
  document.querySelectorAll<HTMLButtonElement>("[data-locale]").forEach((item) => {
    item.setAttribute("aria-pressed", String(item === button));
  });
  renderAll();
});

new ResizeObserver(() => {
  updateGraph(classifyNodes(catalog, selectedIds));
}).observe(stage);

renderAll();
